import { List, ListItem, f7ready } from 'framework7-react';
import {
  CSSProperties,
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { PendingRecurringEvent } from '@shared/types/event';
import dayjs, { Dayjs } from 'dayjs';
import { groupBy, orderBy, uniq } from 'lodash';
import pluralize from 'pluralize';
import { IS_REFER_AND_EARN_ENABLED } from 'src/constants/FeatureFlags';

import {
  useCurrentEntertainerQuery,
  useEntertainerEventRequestsSplashNotificationQuery,
} from '@queries/EntertainerQueries';
import { FeedEvent } from '@queries/EventQueries';
import {
  UserScheduleSubmission,
  UserScheduleSubmissionDateDetail,
  UserScheduleSubmissionUpcomingEvent,
  useCurrentUserScheduleSubmissionQuery,
} from '@queries/UserScheduleSubmissionsQueries';

import {
  useAddEntertainerToEventMutation,
  useCreateEntertainerEventMutation,
  useRespondToEntertainerEventRequestMutation,
} from '@mutations/EntertainerMutations';
import {
  useCreateScheduleDateDetailsMutation,
  useUpdateScheduleDateDetailsMutation,
  useUpdateUserScheduleSubmissionMutation,
} from '@mutations/UserScheduleSubmissionsMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useTimePickerModal } from '@hooks/useTimePickerModal';

import { useNavigate } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { findFirstAlphaCharacter } from '@utilities/findFirstAlphaCharacter';
import { guessTimezone } from '@utilities/guessTimezone';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard from '@components/cards/EventCard';
import { Chip } from '@components/chips/Chip';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { InlineDatePicker } from '@components/layout/InlineDatePicker/InlineDatePicker';
import { LetterAvatar } from '@components/layout/LetterAvatar/LetterAvatar';
import { PopoverActionList } from '@components/layout/PopoverActionList/PopoverActionList';
import { Screen } from '@components/layout/Screen';
import { SeoLink } from '@components/layout/SeoLink/SeoLink';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import {
  CreateEditEventPopup,
  SubmitEventProperties,
} from '@screens/CreateEditEventScreen/CreateEditEventForm';

import { AddCastSizePopup } from './AddCastSizePopup';
import { AddFlyerPopup } from './AddFlyerPopup';
import { ControlledRecurringEventConfirmationPopup } from './RecurringEventConfirmationPopup';

const getFormattedTime = (startTime: string) => {
  const dateTime = dayjs(`2026-01-01 ${startTime}`);
  const hasMinutes = dateTime.minute() !== 0;

  return dateTime.format(hasMinutes ? 'h:mmA' : 'hA');
};

const PendingEventSection: FC<{ events: FeedEvent[] }> = ({ events }) => {
  const { track } = useAnalytics();
  const { entertainer } = useCurrentEntertainerQuery();
  const [hiddenEvents, setHiddenEvents] = useState<string[]>([]);
  const titleText = events.length === 1 ? `1 show pending` : `${events.length} shows pending`;
  const { respondToEntertainerEventRequestAsync } = useRespondToEntertainerEventRequestMutation(
    entertainer?._id,
  );

  const visibleEvents = useMemo(() => {
    return events.filter((event) => !hiddenEvents.includes(event._id));
  }, [events, hiddenEvents]);

  const handleHideEvent = useCallback(async (eventId: string) => {
    track('Hide event', {
      eventId,
    });

    setHiddenEvents((prev) => [...prev, eventId]);
    await respondToEntertainerEventRequestAsync({ eventId, status: 'rejected' });
  }, []);

  const handleConfirmEvents = useCallback(async () => {
    track('Confirm pending events', {
      eventIds: visibleEvents.map((event) => event._id),
    });

    await Promise.all(
      visibleEvents.map((event) =>
        respondToEntertainerEventRequestAsync({ eventId: event._id, status: 'accepted' }),
      ),
    );
  }, [visibleEvents, respondToEntertainerEventRequestAsync]);

  return (
    <div
      className="mb-3"
      style={{ border: '1px solid var(--color-yellow)', borderRadius: 16, padding: 16 }}
    >
      <div
        className="mb-2"
        style={{ borderBottom: '1px solid var(--color-yellow)', paddingBottom: 12 }}
      >
        {titleText}
      </div>
      <List style={{ margin: '0 -1rem' }}>
        {visibleEvents.map((event) => (
          <ListItem key={`pending-event-${event._id}}`}>
            <div
              slot="title"
              onClick={() => {}}
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              <span>{event.title}</span>
            </div>

            <div slot="footer">{event.timeDetails.split(' at ')[1]}</div>

            <div slot="media">
              <div>{dayjs(event.startDate).format('M/D')}</div>
            </div>

            <div slot="after">
              <div className="d-flex gap-2 align-items-center">
                <Button
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  className="py-1 px-2"
                  roundness="rounded"
                  startIcon={<FontAwesomeIcon name="times" />}
                  onClick={() => handleHideEvent(event._id)}
                >
                  Not me
                </Button>
              </div>
            </div>
          </ListItem>
        ))}
      </List>

      <Button className="mt-2" color="yellow" roundness="rounded" onClick={handleConfirmEvents}>
        {visibleEvents.length === 1 ? `Confirm show` : `Confirm all ${visibleEvents.length}`}
      </Button>
    </div>
  );
};

const ParticipationTypeChip = ({ label, color }: { label: string; color: 'yellow' | 'purple' }) => {
  return (
    <Chip
      variant="outlined"
      label={label}
      color={color}
      roundness="rounded"
      size="sm"
      style={{
        fontWeight: 700,
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        height: 18,
        borderRadius: 4,
        padding: '0px 8px !important',
      }}
    />
  );
};

const ReferralInformationCard = ({ currentMonth }: { currentMonth: Dayjs }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  return (
    <div
      className="mb-3"
      style={{
        border: '1px solid var(--color-yellow)',
        backgroundColor: 'rgba(255, 204, 0, 0.1)',
        borderRadius: 16,
        padding: 16,
      }}
      onClick={() => {
        track('Click referral learn more');
        navigate('/');
      }}
    >
      <div className="d-flex gap-3 align-items-center">
        <div
          className="ps-2 pe-2"
          style={{ fontSize: 32, fontWeight: 600, color: 'var(--color-yellow)' }}
        >
          $5
        </div>
        <div>
          <div>
            <div style={{ fontSize: 14 }} className="mb-1">
              <strong>{`Nice — ${dayjs(currentMonth).format('MMMM')}'s complete. Earn for every castmate you refer`}</strong>
            </div>
            <div style={{ color: '#999', fontSize: 12 }}>They get set up, you get paid</div>
          </div>
          <div className="mt-2">
            <div className="d-inline-block">
              <Button
                color="yellow"
                roundness="rounded"
                size="sm"
                className="px-3"
                endIcon={<FontAwesomeIcon name="chevron-right" />}
              >
                Learn more
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface EventListItem {
  castSize?: number;
  isEntertaining: boolean;
  isProducing: boolean;
  eventId?: string;
  scheduleDateDetailsId?: string;
  img?: string;
  startDate: string;
  startTime: string;
  title: string;
  venue: { title: string };
}

const EventList: FC<{
  currentMonth: Dayjs;
  eventRequests: FeedEvent[];
  upcomingEvents: UserScheduleSubmissionUpcomingEvent[];
  confirmedUpcomingDates: string[];
  scheduleDateDetails: Record<string, UserScheduleSubmissionDateDetail[] | undefined>;
  onAddEventDetails: (date: string) => void;
  onAddEvent: (date?: string) => void;
  onViewCalendar: () => void;
}> = ({
  currentMonth,
  eventRequests,
  upcomingEvents,
  confirmedUpcomingDates,
  scheduleDateDetails,
  onAddEventDetails,
  onAddEvent,
  onViewCalendar,
}) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { schedule } = useCurrentUserScheduleSubmissionQuery();
  const { updateScheduleDateDetailsAsync } = useUpdateScheduleDateDetailsMutation();
  const [currentAddFlyerEvent, setCurrentAddFlyerEvent] = useState<EventListItem | undefined>(
    undefined,
  );
  const [currentAddCastSizeEvent, setCurrentAddCastSizeEvent] = useState<EventListItem | undefined>(
    undefined,
  );

  const filteredUpcomingEvents: EventListItem[] = useMemo(() => {
    return orderBy(
      [
        ...(upcomingEvents
          ?.filter((event) => currentMonth.isSame(event.startDate, 'month'))
          ?.map((event) => ({
            ...event,
            startTime:
              event.startTime && event.startTime !== '00:00'
                ? getFormattedTime(event.startTime)
                : '',
            eventId: event._id,
            castSize: -1, // default to not set
            unixTime: dayjs(`${event.startDate} ${event.startTime ?? '00:00'}`).unix(),
          })) ?? []),
        ...Object.entries(scheduleDateDetails)
          .filter(([date]) => currentMonth.isSame(date, 'month'))
          .flatMap(([date, details = []]) =>
            details.map((detail) => ({
              scheduleDateDetailsId: detail._id,
              isEntertaining: detail.isEntertaining,
              img: detail.img,
              isProducing: detail.isProducing,
              startDate: date,
              startTime: detail.startTime ? getFormattedTime(detail.startTime) : '',
              castSize: detail.castSize,
              title: detail.showName ?? '',
              venue: {
                title: detail.venue ?? '',
              },
              unixTime: dayjs(`${date} ${detail.startTime ?? '00:00'}`).unix(),
            })),
          ),
      ],
      'unixTime',
      'asc',
    );
  }, [upcomingEvents, currentMonth, scheduleDateDetails]);

  const filteredEventsGroupedByStartDate = useMemo(() => {
    const now = dayjs();
    const eventsGroupedByStartDate = groupBy(filteredUpcomingEvents, (event) => event.startDate);
    const eventStartDates = Object.keys(eventsGroupedByStartDate);
    const allStartDates = uniq([
      ...eventStartDates,
      ...(confirmedUpcomingDates ?? []).filter((date) => dayjs(date).isSame(currentMonth, 'month')),
    ])
      .filter((date) => now.isSameOrBefore(date, 'day'))
      .sort();

    return Object.fromEntries(
      allStartDates.map((date) => {
        return [date, eventsGroupedByStartDate[date] ?? []];
      }),
    );
  }, [filteredUpcomingEvents, confirmedUpcomingDates, currentMonth]);

  const unsetCount = useMemo(() => {
    return Object.values(filteredEventsGroupedByStartDate).filter((events) => events.length === 0)
      .length;
  }, [filteredEventsGroupedByStartDate]);

  const allDatesFilled = useMemo(() => {
    return Object.keys(filteredEventsGroupedByStartDate).every((date) => {
      return filteredEventsGroupedByStartDate[date].length > 0;
    });
  }, [filteredEventsGroupedByStartDate]);

  const { openTimePickerModal } = useTimePickerModal();
  const handleAddStartTime = (event: EventListItem) => {
    openTimePickerModal({
      startDate: event.startDate,
      toolbarCloseText: 'Save',
      onClose: (date) => {
        if (event.scheduleDateDetailsId) {
          const updatedStartTime = dayjs(date).format('HH:mm');

          updateScheduleDateDetailsAsync({
            scheduleDateDetailsId: event.scheduleDateDetailsId,
            startTime: updatedStartTime,
          });
        }
      },
    });
  };

  const hasMultipleParticipationOptions = (schedule?.participationOptions?.length ?? 0) > 1;

  return (
    <>
      {!!upcomingEvents?.length || !!confirmedUpcomingDates?.length ? (
        <>
          {Object.keys(filteredEventsGroupedByStartDate).length ? (
            <>
              {Boolean(
                !eventRequests?.length &&
                upcomingEvents?.length &&
                allDatesFilled &&
                IS_REFER_AND_EARN_ENABLED,
              ) && <ReferralInformationCard currentMonth={currentMonth} />}

              <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                <div
                  style={{
                    opacity: 0.8,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  <strong>{`Upcoming ${dayjs(currentMonth).format('MMMM')} Shows`}</strong>
                </div>
                <div>
                  <strong>
                    {!!unsetCount && (
                      <>
                        <span style={{ color: 'var(--color-yellow)' }}>
                          {unsetCount} to fill in{' '}
                        </span>
                        {' • '}
                      </>
                    )}
                    <span style={{ opacity: 0.8 }}>{`${filteredUpcomingEvents?.length} set`}</span>
                  </strong>
                </div>
              </div>
              <List style={{ margin: '.5rem -1rem 0' }} dividersIos>
                {Object.entries(filteredEventsGroupedByStartDate).map(([date, events]) =>
                  events.length ? (
                    events.map((event, i) => (
                      <ListItem
                        key={`upcoming-event-${event.eventId}-${event.scheduleDateDetailsId}`}
                      >
                        <div
                          slot="title"
                          onClick={() => {}}
                          style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                        >
                          {hasMultipleParticipationOptions &&
                            (event.isEntertaining || event.isProducing) && (
                              <div className="d-flex gap-1">
                                {event.isEntertaining && (
                                  <ParticipationTypeChip label="Entertaining" color="yellow" />
                                )}
                                {event.isProducing && (
                                  <ParticipationTypeChip label="Producing" color="purple" />
                                )}
                              </div>
                            )}
                          <div
                            style={{
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {event.title}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="item-footer"
                              style={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                              }}
                            >
                              {[event.startTime, event.venue.title].filter(Boolean).join(' • ')}
                            </div>
                            {!event.startTime && (
                              <div>
                                <Button
                                  variant="outlined dashed"
                                  color="yellow"
                                  size="sm"
                                  startIcon={<FontAwesomeIcon name="plus" />}
                                  style={{ padding: '4px 10px', fontSize: 10, letterSpacing: 0.5 }}
                                  onClick={() => handleAddStartTime(event)}
                                >
                                  Add start time
                                </Button>
                              </div>
                            )}
                            {!event.castSize && event.scheduleDateDetailsId && (
                              <div>
                                <Button
                                  variant="outlined dashed"
                                  color="yellow"
                                  size="sm"
                                  startIcon={<FontAwesomeIcon name="plus" />}
                                  style={{ padding: '4px 10px', fontSize: 10, letterSpacing: 0.5 }}
                                  onClick={() => setCurrentAddCastSizeEvent(event)}
                                >
                                  Add cast size
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div slot="media">
                          <div className="d-flex align-items-center justify-content-center">
                            <div style={{ width: 24, textAlign: 'center' }}>
                              {i === 0 && (
                                <div style={{ lineHeight: 1 }}>
                                  <div style={{ fontWeight: 600 }}>{dayjs(date).format('D')}</div>
                                  <div style={{ fontSize: 12 }}>{dayjs(date).format('ddd')}</div>
                                </div>
                              )}
                            </div>
                            <div
                              className="d-flex align-items-center"
                              style={{ marginLeft: 'var(--f7-list-item-media-margin)' }}
                            >
                              {event?.img ? (
                                <img
                                  src={event.img}
                                  alt={event.title}
                                  style={{
                                    width: 46,
                                    height: 58,
                                    objectFit: 'cover',
                                    borderRadius: 4,
                                  }}
                                />
                              ) : (
                                <div
                                  className="d-flex flex-column align-items-center justify-content-center"
                                  style={{
                                    width: 46,
                                    height: 58,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: 8,
                                    border: '1px dashed rgba(255, 255, 255, 0.8)',
                                    fontSize: 7,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.6)',
                                  }}
                                  onClick={() => {
                                    setCurrentAddFlyerEvent(event);
                                  }}
                                >
                                  <div style={{ fontSize: 12 }}>+</div>
                                  <div>Flyer</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem
                      key={`missing-event-details-${date}}`}
                      onClick={() => {
                        onAddEventDetails(date);
                      }}
                    >
                      <div
                        className="item-title py-2 px-3"
                        style={{
                          width: '100%',
                          backgroundColor: 'rgba(255, 204, 0, 0.1)',
                          borderRadius: 12,
                          border: 'var(--color-yellow) 1px dashed',
                        }}
                      >
                        <div>
                          <div className="d-flex align-items-center justify-content-between w-100">
                            <div className="d-grid align-items-center">
                              <div>
                                <Chip
                                  color="yellow"
                                  variant="outlined"
                                  roundness="rounded"
                                  size="sm"
                                  label={
                                    <strong
                                      style={{
                                        textTransform: 'uppercase',
                                        fontSize: 10,
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      Pencilled in
                                    </strong>
                                  }
                                />
                              </div>
                              <div>Add the details</div>
                              <div className="item-footer">
                                <div slot="footer">Venue &amp; show name</div>
                              </div>
                            </div>

                            <div>
                              <IconButton variant="flat" color="yellow" roundness="rounded">
                                <FontAwesomeIcon name="plus" />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div slot="media">
                        <div style={{ width: 24, textAlign: 'center' }}>
                          <div style={{ lineHeight: 1 }}>
                            <div style={{ fontWeight: 600 }}>{dayjs(date).format('D')}</div>
                            <div style={{ fontSize: 12 }}>{dayjs(date).format('ddd')}</div>
                          </div>
                        </div>
                      </div>
                    </ListItem>
                  ),
                )}
              </List>

              <Button
                className="mt-2"
                color="neutral"
                roundness="rounded"
                variant="outlined dashed"
                startIcon={<FontAwesomeIcon name="plus" />}
                onClick={() => {
                  onAddEvent();
                }}
              >
                Add a show
              </Button>
              <Button
                className="mt-1"
                color="neutral"
                roundness="rounded"
                variant="flat"
                onClick={onViewCalendar}
              >
                View calendar
              </Button>
            </>
          ) : (
            <>
              <EmptyState
                title={`No upcoming ${dayjs(currentMonth).format('MMMM')} shows`}
                text="You have no upcoming shows this month"
                button={{
                  text: 'Add a show',
                  variant: 'outlined',
                  startIcon: <FontAwesomeIcon name="plus" />,
                  color: 'neutral',
                  roundness: 'rounded',
                  onClick: () => {
                    onAddEvent();
                  },
                }}
                secondaryButton={{
                  className: 'mt-0',
                  variant: 'flat',
                  text: 'View calendar',
                  onClick: onViewCalendar,
                }}
              />
            </>
          )}
        </>
      ) : (
        <EmptyState
          title="No upcoming shows"
          text="You have no upcoming shows"
          button={{ text: 'View calendar', onClick: onViewCalendar }}
        />
      )}
      <AddFlyerPopup
        isVisible={!!currentAddFlyerEvent}
        onClose={() => setCurrentAddFlyerEvent(undefined)}
        scheduleDateDetailsId={currentAddFlyerEvent?.scheduleDateDetailsId}
        eventId={currentAddFlyerEvent?.eventId}
      />
      <AddCastSizePopup
        isVisible={!!currentAddCastSizeEvent}
        onClose={() => setCurrentAddCastSizeEvent(undefined)}
        scheduleDateDetailsId={currentAddCastSizeEvent?.scheduleDateDetailsId}
      />
    </>
  );
};

interface RadioOption {
  value: string;
  label: string;
  selectedColor: 'default' | 'purple' | 'white';
}

interface RadioOptionsProps {
  values: string[];
  options: RadioOption[];
  onChange: (value: string) => void;
  otherLabel?: string;
  onOtherClick?: () => void;
}

const SELECTED_STYLES: Record<RadioOption['selectedColor'], CSSProperties> = {
  default: {
    background: 'rgba(245,185,33,.1)',
    borderColor: 'rgba(245,185,33,.45)',
    color: '#F5B921',
  },
  purple: {
    background: 'rgba(139,92,246,.13)',
    borderColor: 'rgba(139,92,246,.5)',
    color: '#8B5CF6',
  },
  white: {
    background: 'rgba(255,255,255,1)',
    borderColor: 'rgba(255,255,255,1)',
    color: '#343434',
  },
};

const RadioOptions = ({
  values,
  options,
  onChange,
  onOtherClick,
  otherLabel,
}: RadioOptionsProps) => {
  const isOtherSelected = Boolean(
    values.length && !options.some(({ value }) => values.includes(value)),
  );

  return (
    <div className="d-flex gap-2 flex-wrap mt-1">
      {options.map((option) => (
        <div
          key={option.value}
          style={{
            background: '#000',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: '#343434',
            borderRadius: 999,
            padding: '9px 14px',
            color: '#8c8c8c',
            fontSize: 13.5,
            fontWeight: 600,
            cursor: 'pointer',
            transition: '.14s',
            ...(values.includes(option.value) ? SELECTED_STYLES[option.selectedColor] : {}),
          }}
          onClick={() => onChange(option.value)}
        >
          <div style={{ whiteSpace: 'nowrap' }}>{option.label}</div>
        </div>
      ))}
      {onOtherClick && (
        <div
          style={{
            background: '#000',
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: '#343434',
            borderRadius: 999,
            padding: '9px 14px',
            color: '#8c8c8c',
            fontSize: 13.5,
            fontWeight: 600,
            cursor: 'pointer',
            transition: '.14s',
            ...(isOtherSelected ? SELECTED_STYLES['default'] : {}),
          }}
          onClick={onOtherClick}
        >
          <div className="d-flex align-items-center gap-2">
            <div style={{ whiteSpace: 'nowrap' }}>{isOtherSelected ? otherLabel : 'Other'}</div>
            {isOtherSelected && <FontAwesomeIcon name="chevron-down" />}
          </div>
        </div>
      )}
    </div>
  );
};

interface ParticipationOptionTypeTextProps {
  selectedOption: { title: string };
  options: { _id: string; title: string }[];
  type: 'entertainer' | 'producer';
  onChange: (value: string) => void;
}

const ParticipationOptionTypeText = ({
  selectedOption,
  options,
  type,
  onChange,
}: ParticipationOptionTypeTextProps) => {
  const showPicker = options.length > 1;

  const label = (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      <strong>{selectedOption.title}</strong>
      {showPicker && <FontAwesomeIcon name="chevron-down" style={{ fontSize: 10 }} />}
    </span>
  );

  const buttons = options.map((option) => ({
    text: option.title,
    onClick: () => onChange(option._id),
  }));

  return (
    <>
      {options.length > 1 ? (
        <PopoverActionList className="d-inline-flex" buttons={buttons}>
          {label}
        </PopoverActionList>
      ) : (
        label
      )}
      {` (${type})`}
    </>
  );
};

interface ParticipationOptionSelectorProps {
  schedule: UserScheduleSubmission;
  selectedEntertainerIds: string[];
  selectedPromoterIds: string[];
  onEntertainerChange: (updatedEntertainerIds: string[]) => void;
  onPromoterChange: (updatedPromoterIds: string[]) => void;
}

const ParticipationOptionSelector = ({
  schedule,
  selectedEntertainerIds,
  selectedPromoterIds,
  onEntertainerChange,
  onPromoterChange,
}: ParticipationOptionSelectorProps) => {
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      const hasOneParticipationOption = schedule?.participationOptions?.length === 1;

      if (hasOneParticipationOption) {
        const [participationOption] = schedule.participationOptions ?? [];

        if (participationOption) {
          if (participationOption?.type === 'entertainer') {
            onEntertainerChange([participationOption._id]);
          }

          if (participationOption?.type === 'promoter') {
            onPromoterChange([participationOption._id]);
          }
        }
      }
    }
  }, []);

  const values = useMemo(() => {
    return [
      ...(selectedEntertainerIds.length ? ['entertainer'] : []),
      ...(selectedPromoterIds.length ? ['promoter'] : []),
    ];
  }, [selectedEntertainerIds, selectedPromoterIds]);

  const participationTypeOptions: RadioOption[] = [
    {
      value: 'entertainer',
      label: 'Entertainer',
      selectedColor: 'default',
    },
    {
      value: 'promoter',
      label: 'Producer',
      selectedColor: 'purple',
    },
  ];

  const handleChange = (value: string) => {
    if (value === 'entertainer') {
      if (selectedEntertainerIds.length) {
        onEntertainerChange([]);
      } else {
        const firstEntertainer = schedule?.participationOptions?.find(
          ({ type }) => type === 'entertainer',
        );
        firstEntertainer && onEntertainerChange([firstEntertainer._id]);
      }
    }

    if (value === 'promoter') {
      if (selectedPromoterIds.length) {
        onPromoterChange([]);
      } else {
        const firstPromoter = schedule?.participationOptions?.find(
          ({ type }) => type === 'promoter',
        );
        firstPromoter && onPromoterChange([firstPromoter._id]);
      }
    }
  };

  const participationOptionsText = useMemo<ReactNode>(() => {
    const entertainerOptions =
      schedule?.participationOptions?.filter(({ type }) => type === 'entertainer') ?? [];
    const promoterOptions =
      schedule?.participationOptions?.filter(({ type }) => type === 'promoter') ?? [];

    const selectedEntertainer = entertainerOptions?.find(({ _id }) =>
      selectedEntertainerIds.includes(_id),
    );
    const selectedPromoter = promoterOptions?.find(({ _id }) => selectedPromoterIds.includes(_id));

    if (selectedEntertainer || selectedPromoter) {
      return (
        <div>
          Goes on{' '}
          {[
            selectedEntertainer && (
              <ParticipationOptionTypeText
                selectedOption={selectedEntertainer}
                options={entertainerOptions}
                type="entertainer"
                onChange={(updatedValue) => onEntertainerChange([updatedValue])}
              />
            ),
            selectedEntertainer && selectedPromoter && ' and ',
            selectedPromoter && (
              <ParticipationOptionTypeText
                selectedOption={selectedPromoter}
                options={promoterOptions}
                type="producer"
                onChange={(updatedValue) => onPromoterChange([updatedValue])}
              />
            ),
          ].filter(Boolean)}
        </div>
      );
    }

    return <div>Pick at least one so we know where this goes.</div>;
  }, [values, selectedEntertainerIds, selectedPromoterIds, schedule?.participationOptions]);

  if (schedule?.participationOptions?.length === 1) {
    return <></>;
  }

  return (
    <div>
      <FormLabel text="What's your part in this show?" />
      <RadioOptions values={values} options={participationTypeOptions} onChange={handleChange} />
      <div
        className="d-flex align-items-center gap-2 py-2 px-3 mt-2"
        style={{
          background: '#171717',
          border: '1px solid #242424',
          borderRadius: 12,
          fontSize: 12,
        }}
      >
        <div>
          <FontAwesomeIcon name="info-circle" />
        </div>
        {participationOptionsText}
      </div>
    </div>
  );
};

const AddEventDetails: FC<{
  selectedDate?: string;
  existingDetail?: UserScheduleSubmissionDateDetail;
  onBack: () => void;
}> = ({ selectedDate, existingDetail, onBack }) => {
  const { schedule } = useCurrentUserScheduleSubmissionQuery();
  const { track } = useAnalytics();
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const [entertainerIds, setEntertainerIds] = useState<string[]>(
    existingDetail?.entertainerIds ?? [],
  );
  const [promoterIds, setPromoterIds] = useState<string[]>(existingDetail?.promoterIds ?? []);
  const [startTime, setStartTime] = useState<string | undefined>(existingDetail?.startTime);
  const [showName, setShowName] = useState(existingDetail?.showName ?? '');
  const [venue, setVenue] = useState(existingDetail?.venue ?? '');
  const [castSizeError, setCastSizeError] = useState(false);
  const [castSize, setCastSize] = useState<number | undefined>(existingDetail?.castSize);
  const { isSubmitting: isCreating, createScheduleDateDetailsAsync } =
    useCreateScheduleDateDetailsMutation();
  const { isSubmitting: isUpdating, updateScheduleDateDetailsAsync } =
    useUpdateScheduleDateDetailsMutation();
  const isSubmitting = isCreating || isUpdating;

  useLayoutEffect(() => {
    if (firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, []);

  const handleBack = useCallback(() => {
    track('Back to event list');
    onBack();
  }, [onBack]);

  const handleAddShow = useCallback(async () => {
    if (!selectedDate) {
      return;
    }

    track('Add Show Details', {
      date: selectedDate,
    });

    if (existingDetail?._id) {
      await updateScheduleDateDetailsAsync({
        scheduleDateDetailsId: existingDetail._id,
        showName,
        venue,
        entertainerIds,
        promoterIds,
        startTime,
        castSize,
      });
    } else {
      await createScheduleDateDetailsAsync({
        date: selectedDate,
        showName,
        venue,
        entertainerIds,
        promoterIds,
        startTime,
        castSize,
      });
    }
    onBack();
  }, [
    selectedDate,
    entertainerIds,
    promoterIds,
    startTime,
    existingDetail,
    showName,
    venue,
    createScheduleDateDetailsAsync,
    updateScheduleDateDetailsAsync,
    onBack,
  ]);

  const timeOptions: RadioOption[] = [
    '11:00',
    '13:00',
    '19:00',
    '20:00',
    '21:00',
    '22:00',
    '22:30',
  ].map((time) => ({
    value: time,
    label: getFormattedTime(time),
    selectedColor: 'default',
  }));

  const hasParticipation = Boolean(entertainerIds.length || promoterIds.length);

  const { openTimePickerModal } = useTimePickerModal();
  const handleOtherTimeClick = () => {
    selectedDate &&
      openTimePickerModal({
        startDate: selectedDate,
        startTime,
        onClose: (date) => setStartTime(dayjs(date).format('HH:mm')),
      });
  };

  return (
    <div
      style={{
        border: '2px solid rgba(255, 255, 255, 0.2)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div>
        <div className="d-flex gap-2 align-items-center mb-3">
          <div className="px-2" style={{ lineHeight: 1, textAlign: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: 24, color: 'var(--color-yellow)' }}>
              {dayjs(selectedDate).format('D')}
            </div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', opacity: 0.8 }}>
              {dayjs(selectedDate).format('ddd')}
            </div>
          </div>
          <div>
            <div
              className="text-uppercase"
              style={{ color: 'var(--color-yellow)', letterSpacing: 0.5 }}
            >
              <strong>Pencilled in</strong>
            </div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Add the details for this day</div>
          </div>
        </div>
        <div className="d-grid gap-2">
          {schedule && (
            <ParticipationOptionSelector
              schedule={schedule}
              selectedEntertainerIds={entertainerIds}
              selectedPromoterIds={promoterIds}
              onEntertainerChange={setEntertainerIds}
              onPromoterChange={setPromoterIds}
            />
          )}
          <div>
            <FormLabel text="Show name" />
            <Input
              ref={firstFieldRef}
              placeholder="eg. Big Drag Energy"
              style={{ background: '#000', border: '1px solid rgba(255, 255, 255, 0.2)' }}
              onChange={(e) => setShowName(e.target.value)}
            />
          </div>
          <div>
            <FormLabel text="Venue" />
            <Input
              placeholder="eg. Da Clerb"
              style={{ background: '#000', border: '1px solid rgba(255, 255, 255, 0.2)' }}
              onChange={(e) => setVenue(e.target.value)}
            />
          </div>
          <div>
            <FormLabel text="Start time — optional" />
            <RadioOptions
              values={startTime ? [startTime] : []}
              options={timeOptions}
              onChange={setStartTime}
              otherLabel={startTime ? getFormattedTime(startTime) : undefined}
              onOtherClick={handleOtherTimeClick}
            />
          </div>
          <div>
            <FormLabel text="Cast size — optional" />
            <Input
              placeholder="eg. 4"
              type="number"
              min={1}
              style={{
                background: '#000',
                border: castSizeError
                  ? '1px solid var(--color-red)'
                  : '1px solid rgba(255, 255, 255, 0.2)',
              }}
              onChange={(e) => {
                try {
                  const value = e.target.value.trim();
                  setCastSizeError(false);

                  if (value) {
                    const parsedValue = value ? parseInt(value) : undefined;

                    if (parsedValue && (isNaN(parsedValue) || parsedValue < 1)) {
                      setCastSize(undefined);
                      setCastSizeError(true);
                      return;
                    }

                    setCastSize(parsedValue);
                  }
                } catch (e) {
                  setCastSize(undefined);
                  setCastSizeError(true);
                }
              }}
            />
          </div>
        </div>

        <div className="mt-3">
          <Button
            color="yellow"
            roundness="rounded"
            disabled={!hasParticipation || !showName.trim() || !venue.trim() || isSubmitting}
            onClick={handleAddShow}
          >
            Add this show
          </Button>
          <Button color="neutral" roundness="rounded" variant="flat" onClick={handleBack}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
};

const IS_PARTICIPATION_FILTER_VISIBLE = false;

const EventListSection: FC<{
  initialSelectedDate?: string;
  confirmedUpcomingDates?: string[];
  scheduleDateDetails: Record<string, UserScheduleSubmissionDateDetail[] | undefined>;
  onViewCalendar: () => void;
}> = ({ initialSelectedDate, confirmedUpcomingDates, scheduleDateDetails, onViewCalendar }) => {
  const { track } = useAnalytics();
  const { eventRequests } = useCurrentEntertainerQuery();
  const { schedule } = useCurrentUserScheduleSubmissionQuery();
  const [currentMonth, setCurrentMonth] = useState(
    dayjs(initialSelectedDate ? initialSelectedDate : confirmedUpcomingDates?.sort()?.[0]).startOf(
      'month',
    ),
  );
  const [selectedDate, setSelectedDate] = useState<string | undefined>(initialSelectedDate);

  const isEarliestMonth = useMemo(() => {
    return currentMonth.isSame(dayjs().startOf('month'), 'month');
  }, [currentMonth]);

  const handleAddEventDetails = useCallback((date: string) => {
    track('Add Event Details', {
      date,
    });
    setSelectedDate(date);
  }, []);

  const [selectedParticipationFilter, setSelectedParticipationFilter] = useState<string>('all');
  const participationFilters = useMemo<RadioOption[]>(() => {
    return [
      {
        label: 'All',
        value: 'all',
        selectedColor: 'white',
      },
      ...(schedule?.participationOptions?.map<RadioOption>((option) => ({
        label: option.title,
        value: option._id,
        selectedColor: 'white',
      })) ?? []),
    ];
  }, [schedule?.participationOptions]);

  const handleAddEvent = useCallback((date?: string) => {
    if (!date) {
      f7ready((f7) => {
        const calendarModal = f7.calendar.create({
          openIn: 'customModal',
          header: true,
          toolbar: true,
          footer: true,
          toolbarCloseText: 'Continue',
          dateFormat: 'MM d, yyyy',
          minDate: dayjs(date).startOf('day').toDate(),
          on: {
            close: (calendar) => {
              const [date] = calendar.getValue() ?? [];

              if (date) {
                setCurrentMonth(dayjs(date).startOf('month'));
                setSelectedDate(dayjs(date).format('YYYY-MM-DD'));
              }
              calendar.destroy();
            },
          },
        });

        track('Add Event', {
          date,
        });

        calendarModal.open();
      });
    }
  }, []);

  return (
    <>
      {selectedDate ? (
        <>
          <div
            className="d-flex align-items-center mb-2"
            style={{ marginLeft: -8 }}
            onClick={() => {
              track('Back to event list');
              setSelectedDate(undefined);
            }}
          >
            <IconButton variant="flat" color="neutral">
              <FontAwesomeIcon name="chevron-left" />
            </IconButton>
            <span style={{ color: 'var(--color-yellow)' }}>{currentMonth.format('MMMM YYYY')}</span>
          </div>

          <AddEventDetails
            selectedDate={selectedDate}
            existingDetail={undefined}
            onBack={() => setSelectedDate(undefined)}
          />
        </>
      ) : (
        <>
          <div className="d-flex align-items-center mb-2" style={{ marginLeft: -8 }}>
            <IconButton
              variant="flat"
              color="neutral"
              disabled={isEarliestMonth}
              onClick={() => {
                track('Navigate to previous month');
                setCurrentMonth(currentMonth.subtract(1, 'month'));
              }}
            >
              <FontAwesomeIcon name="chevron-left" />
            </IconButton>
            <span style={{ color: 'var(--color-yellow)' }}>{currentMonth.format('MMMM YYYY')}</span>
            <IconButton
              variant="flat"
              color="neutral"
              onClick={() => {
                track('Navigate to next month');
                setCurrentMonth(currentMonth.add(1, 'month'));
              }}
            >
              <FontAwesomeIcon name="chevron-right" />
            </IconButton>
          </div>

          {IS_PARTICIPATION_FILTER_VISIBLE && (schedule?.participationOptions?.length ?? 0) > 1 && (
            <HorizontalScroll
              items={[
                <div className="d-flex pb-2">
                  <RadioOptions
                    values={[selectedParticipationFilter]}
                    options={participationFilters}
                    onChange={setSelectedParticipationFilter}
                  />
                </div>,
              ]}
            />
          )}

          {!!eventRequests?.length && <PendingEventSection events={eventRequests} />}

          <EventList
            currentMonth={currentMonth}
            eventRequests={eventRequests ?? []}
            upcomingEvents={schedule?.upcomingEvents ?? []}
            confirmedUpcomingDates={confirmedUpcomingDates ?? []}
            scheduleDateDetails={scheduleDateDetails}
            onAddEventDetails={handleAddEventDetails}
            onAddEvent={handleAddEvent}
            onViewCalendar={onViewCalendar}
          />
        </>
      )}
    </>
  );
};

const CalendarConfirmationSection: FC<{
  upcomingEvents: UserScheduleSubmissionUpcomingEvent[];
  initialSelectedDates: string[];
  onConfirm: (dates: string[]) => void;
}> = ({ upcomingEvents, initialSelectedDates, onConfirm }) => {
  const { track } = useAnalytics();
  const eventsGroupedByStartDate = useMemo(() => {
    return groupBy(upcomingEvents, (event) => event.startDate);
  }, [upcomingEvents]);

  const [datePickerRecord, setDatePickerRecord] = useState<Record<string, boolean>>(
    Object.fromEntries(initialSelectedDates.map((date) => [date, true])),
  );

  const mergedSelectedDates = useMemo(() => {
    return {
      ...datePickerRecord,
      ...Object.fromEntries(Object.keys(eventsGroupedByStartDate).map((date) => [[date], true])),
    };
  }, [eventsGroupedByStartDate, datePickerRecord]);

  const selectedDates = useMemo(() => {
    const now = dayjs();
    return Object.entries(mergedSelectedDates)
      .filter(([date, selected]) => selected && now.isSameOrBefore(date, 'day'))
      .map(([date]) => date);
  }, [mergedSelectedDates]);

  const handleConfirm = useCallback(() => {
    track('Confirm Calendar Dates', {
      selectedDates,
    });
    onConfirm(selectedDates);
  }, [selectedDates, onConfirm]);

  const selectedDatesCount = Object.keys(selectedDates).length;

  return (
    <>
      <p className="mt-0 mb-2">
        <strong>How many shows do you have coming up?</strong> Use the calendar below to select
        which dates you're gonna be performing.
      </p>
      <p className="mt-0 mb-3">
        Have more than one on some dates? Don't worry, you can clarify how many in the next step.
      </p>
      <Card>
        <div className="pb-3">
          <InlineDatePicker
            events={Object.keys(eventsGroupedByStartDate).map((date) => ({
              date: dayjs(date).toDate(),
              color: 'var(--f7-theme-color)',
            }))}
            minDate={dayjs().startOf('day').toISOString()}
            value={mergedSelectedDates}
            onChange={setDatePickerRecord}
          />
        </div>
      </Card>

      <Button className="mt-3" roundness="rounded" onClick={handleConfirm}>
        {selectedDatesCount
          ? `Performing on ${selectedDatesCount} ${pluralize('date', selectedDatesCount)}`
          : 'I have no upcoming shows'}
      </Button>
    </>
  );
};

interface ManageUserScheduleSubmissionsScreenProps {}

interface ManageUserScheduleSubmissionsScreenQueryParams {
  addDateDetails?: string;
}

export const ManageUserScheduleSubmissionsScreen = createScreenComponent<
  ManageUserScheduleSubmissionsScreenProps,
  ManageUserScheduleSubmissionsScreenQueryParams
>(({ f7route }) => {
  const { addDateDetails } = f7route.query ?? {};

  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { entertainerIsReady, refetchEntertainer, entertainer } = useCurrentEntertainerQuery();
  const { schedule } = useCurrentUserScheduleSubmissionQuery();
  useEntertainerEventRequestsSplashNotificationQuery({
    skipReferralSplashNotification: true,
  });
  const { updateUserScheduleSubmissionAsync } = useUpdateUserScheduleSubmissionMutation();

  const [didConfirmCalendar, setDidConfirmCalendar] = useState<boolean>(!!addDateDetails);
  const [confirmedUpcomingDates, setConfirmedUpcomingDates] = useState<string[]>();
  const [isCalendarViewVisible, setIsCalendarViewVisible] = useState<boolean>(!addDateDetails);

  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && !!schedule) {
      didInit.current = true;
      setIsCalendarViewVisible(Boolean(addDateDetails ? false : !schedule?.didSubmitThisWeek));
      setDidConfirmCalendar(
        Boolean(!!addDateDetails ? !!addDateDetails : schedule?.didSubmitThisWeek),
      );
      setConfirmedUpcomingDates(schedule?.dates ?? []);
    }
  }, [schedule]);

  const handleConfirmCalendarDates = useCallback(
    async (dates: string[]) => {
      setIsCalendarViewVisible(false);
      setDidConfirmCalendar(true);
      setConfirmedUpcomingDates(dates);
      await updateUserScheduleSubmissionAsync({
        timezone: guessTimezone(),
        dates,
      });
      await refetchEntertainer();
    },
    [updateUserScheduleSubmissionAsync, refetchEntertainer],
  );

  const handleViewCalendar = useCallback(() => {
    track('View Calendar');
    setIsCalendarViewVisible(true);
  }, []);

  return (
    <Screen
      name="Manage User Schedule Submissions Screen"
      hideToolbar
      headerProps={{
        title: <Screen.GagglTitle />,
      }}
    >
      <Screen.Content>
        {entertainerIsReady && entertainer?._id && schedule ? (
          <>
            <div
              style={{
                opacity: 0.8,
                textTransform: 'uppercase',
                fontSize: 12,
                letterSpacing: 0.5,
                marginBottom: -8,
              }}
            >
              <strong>My Schedule</strong>
            </div>
            <TitleToolbar
              className="mt-0"
              titleTagStyle={{ margin: 0, maxWidth: '100%' }}
              text={
                <div className="d-flex gap-2 align-items-center">
                  <div
                    className="py-2"
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {entertainer.title}
                  </div>
                  {(didConfirmCalendar || schedule?.didSubmitThisWeek) && (
                    <IconButton
                      variant="smooth"
                      color="neutral"
                      onClick={() => {
                        track('Manage Entertainer', {
                          entertainerId: entertainer?._id,
                        });
                        navigate(`/manage/entertainer`, {
                          state: {
                            back: `/manage/entertainer/schedule`,
                          },
                        });
                      }}
                    >
                      <FontAwesomeIcon name="chevron-right" />
                    </IconButton>
                  )}
                </div>
              }
            />

            {isCalendarViewVisible ? (
              <CalendarConfirmationSection
                upcomingEvents={schedule?.upcomingEvents ?? []}
                initialSelectedDates={confirmedUpcomingDates ?? schedule?.dates ?? []}
                onConfirm={handleConfirmCalendarDates}
              />
            ) : (
              <EventListSection
                initialSelectedDate={addDateDetails}
                confirmedUpcomingDates={confirmedUpcomingDates ?? schedule?.dates}
                scheduleDateDetails={schedule?.dateDetails ?? {}}
                onViewCalendar={handleViewCalendar}
              />
            )}

            <ControlledRecurringEventConfirmationPopup />
          </>
        ) : (
          <>
            <Skeleton height={100} />
            <Skeleton height={400} />
          </>
        )}
      </Screen.Content>
    </Screen>
  );
});
